// Full-resolution export: the design is rasterized into Smart Object content
// space on the main thread (high-quality canvas resampling), then rendered at
// full PSD resolution by the worker.

import { drawPlaced } from "./placement";

// Canvas area limit that is safe in all browsers (Safari: 16.7 MP).
const MAX_CANVAS_AREA = 16_000_000;

export function rasterizeDesign(design, designWidth, designHeight) {
  const scale = Math.min(1, Math.sqrt(MAX_CANVAS_AREA / (designWidth * designHeight)));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(designWidth * scale));
  canvas.height = Math.max(1, Math.round(designHeight * scale));
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  drawPlaced(ctx, design.source, design.placement, scale);
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  canvas.width = canvas.height = 0;
  return { width: image.width, height: image.height, data: image.data };
}

export function pixelsToBlob({ pixels, width, height }, type = "image/jpeg", quality = 0.92) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").putImageData(new ImageData(new Uint8ClampedArray(pixels.buffer, pixels.byteOffset, pixels.byteLength), width, height), 0, 0);
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => {
      canvas.width = canvas.height = 0;
      blob ? resolve(blob) : reject(new Error("Could not encode the image."));
    }, type, quality),
  );
}

/** Renders one design on the template at full resolution. Returns a JPEG Blob. */
export async function exportDesign(engine, docId, template, design, appearance, onProgress) {
  const art = rasterizeDesign(design, template.designWidth, template.designHeight);
  const out = await engine.exportImage(docId, art, appearance, { type: "image/jpeg", quality: 0.92 }, onProgress);
  return out.blob || pixelsToBlob(out);
}

export const cleanFileName = (name) => String(name || "mockup").replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();

export function randomSuffix(length = 6) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let out = "";
  for (let i = 0; i < length; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
  return out;
}

export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Decodes an uploaded image into canvases for export and for the GPU. */
export async function loadDesignFile(file, { maxSource = 4096, maxTexture = 2048 } = {}) {
  let image, close = () => {};
  try {
    image = await createImageBitmap(file);
    close = () => image.close?.();
  } catch {
    // SVG and some formats are not supported by createImageBitmap.
    const url = URL.createObjectURL(file);
    image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Could not read “${file.name}”.`));
      img.src = url;
    }).finally(() => setTimeout(() => URL.revokeObjectURL(url), 0));
  }
  const w = image.naturalWidth || image.width, h = image.naturalHeight || image.height;
  if (!w || !h) throw new Error(`Could not read “${file.name}”.`);
  const toCanvas = (max) => {
    const s = Math.min(1, max / Math.max(w, h));
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(w * s));
    c.height = Math.max(1, Math.round(h * s));
    const ctx = c.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(image, 0, 0, c.width, c.height);
    return c;
  };
  const source = toCanvas(maxSource);
  const texture = Math.max(w, h) > maxTexture ? toCanvas(maxTexture) : source;
  close();
  return { source, texture, naturalWidth: w, naturalHeight: h };
}
